import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, Label, StatusChip } from '@smena/ui';
import { TimeSeriesCard } from '@/components/Chart';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { StatTile } from '@/components/StatTile';
import { fetchLlmAnalytics, fetchOverview } from '@/features/analytics/api';

const RANGES = [7, 14, 30, 90];

export function OverviewPage() {
  const [days, setDays] = useState(30);

  // Polling вместо SSE: шина событий сейчас single-account и не персистится
  // (apps/api/src/modules/events/event-bus.ts) - опираться на неё в cross-account вьюхе нельзя.
  const overviewQ = useQuery({
    queryKey: ['overview', days],
    queryFn: () => fetchOverview(days),
    refetchInterval: 30_000,
    staleTime: 5_000,
  });
  const llmQ = useQuery({
    queryKey: ['llm-analytics', days],
    queryFn: () => fetchLlmAnalytics(days),
    refetchInterval: 30_000,
    staleTime: 5_000,
  });

  const o = overviewQ.data;
  const llm = llmQ.data;
  const fallbackPct =
    llm && llm.totals.calls > 0 ? Math.round((llm.totals.fallbacks / llm.totals.calls) * 100) : 0;

  return (
    <>
      <PageHeader
        title="Обзор"
        subtitle="Сводка по всем аккаунтам. Обновляется каждые 30 c."
        actions={
          <div className="flex items-center gap-2">
            <Label htmlFor="range">Период</Label>
            <select
              id="range"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="h-9 rounded-md border border-line-control bg-input px-2 text-sm text-text"
            >
              {RANGES.map((d) => (
                <option key={d} value={d}>
                  {d} дней
                </option>
              ))}
            </select>
          </div>
        }
      />

      <QueryState isLoading={overviewQ.isLoading} error={overviewQ.error}>
        {o ? (
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <StatTile
                label="Аккаунты"
                value={o.accounts.total}
                hint={`${o.accounts.client} боевых · ${o.accounts.demo} demo`}
              />
              <StatTile label="Объекты" value={o.properties} hint={`гостей в CRM: ${o.guests}`} />
              <StatTile
                label="Брони"
                value={o.bookings.total}
                hint={`сегодня ${o.bookings.today} · 7д ${o.bookings.last7} · 30д ${o.bookings.last30}`}
              />
              <StatTile
                label="Открытых эскалаций"
                value={o.escalations.open}
                tone={o.escalations.open > 0 ? 'warn' : 'default'}
                hint={`за 30 дней всего ${o.escalations.last30}`}
              />
              <StatTile
                label="Диалоги"
                value={o.sessions.total}
                hint={`активных за 7 дней: ${o.sessions.activeLast7}`}
              />
              <StatTile
                label="Сообщения"
                value={o.messages.total}
                hint={`за 30 дней: ${o.messages.last30}`}
              />
              <StatTile
                label="LLM-вызовы"
                value={llm?.totals.calls ?? 0}
                tone={fallbackPct > 10 ? 'danger' : fallbackPct > 0 ? 'warn' : 'default'}
                hint={`фолбэков ${fallbackPct}% · средняя ${llm?.totals.avgLatencyMs ?? 0} мс`}
              />
              <StatTile
                label="Токены за период"
                value={((llm?.totals.promptTokens ?? 0) + (llm?.totals.completionTokens ?? 0)).toLocaleString('ru-RU')}
                hint={`из кэша: ${(llm?.totals.cacheHitTokens ?? 0).toLocaleString('ru-RU')}`}
              />
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <TimeSeriesCard
                title="Сообщения по дням"
                subtitle="Все каналы всех аккаунтов"
                data={o.series.messages}
                series={[{ key: 'n', label: 'Сообщений', color: 'var(--color-accent-fg)' }]}
              />
              <TimeSeriesCard
                title="Брони по дням"
                subtitle="По дате создания записи (инжест или ручной ввод)"
                data={o.series.bookings}
                series={[{ key: 'n', label: 'Броней', color: 'var(--color-green)' }]}
              />
              <TimeSeriesCard
                title="Эскалации по дням"
                subtitle="Всё, что конвейер отдал человеку"
                data={o.series.escalations}
                series={[{ key: 'n', label: 'Эскалаций', color: 'var(--color-amber)' }]}
              />
              <TimeSeriesCard
                title="LLM: вызовы и фолбэки"
                subtitle="Рост фолбэков = проблемы с провайдером, ответы уходят на mock-правила (L0)"
                kind="line"
                data={llm?.byDay ?? []}
                series={[
                  { key: 'calls', label: 'Вызовы', color: 'var(--color-accent-fg)' },
                  { key: 'fallbacks', label: 'Фолбэки', color: 'var(--color-destructive)' },
                ]}
              />
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>LLM по аккаунтам</CardTitle>
                  <p className="text-xs text-muted">
                    Стоимость в рублях - V2: нужен конфиг цены за 1K токенов.
                  </p>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 text-sm">
                  {(llm?.byAccount.length ?? 0) === 0 ? (
                    <p className="text-muted">За период вызовов не было.</p>
                  ) : null}
                  {llm?.byAccount.map((a) => (
                    <div
                      key={a.accountId}
                      className="flex items-center justify-between gap-3 border-b border-line/60 pb-2 last:border-0"
                    >
                      <Link to={`/accounts/${a.accountId}`} className="truncate text-accent-fg hover:underline">
                        {a.name}
                      </Link>
                      <span className="shrink-0 text-xs text-muted">
                        {a.calls} выз. · {a.totalTokens.toLocaleString('ru-RU')} ток.
                        {a.fallbacks ? ` · ${a.fallbacks} фолб.` : ''}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Подтверждения действий</CardTitle>
                  <p className="text-xs text-muted">
                    Очередь confirm-режима автономности по всем аккаунтам.
                  </p>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2 text-sm">
                  {Object.keys(o.pendingActions).length === 0 ? (
                    <p className="text-muted">Запросов не было.</p>
                  ) : null}
                  {Object.entries(o.pendingActions).map(([status, n]) => (
                    <StatusChip
                      key={status}
                      tone={
                        status === 'executed'
                          ? 'green'
                          : status === 'pending'
                            ? 'amber'
                            : status === 'expired' || status === 'rejected'
                              ? 'red'
                              : 'muted'
                      }
                    >
                      {status}: {n}
                    </StatusChip>
                  ))}
                  {llm?.byModel.length ? (
                    <div className="mt-3 w-full border-t border-line pt-3 text-xs text-muted">
                      Модели за период:{' '}
                      {llm.byModel.map((m) => `${m.model} (${m.calls})`).join(', ')}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : null}
      </QueryState>
    </>
  );
}
